import { createGlobalStyle } from "styled-components";

// Design System for Les Pilliers
export const theme = {
  colors: {
    primary: "#1E1E1E",      // Deep Charcoal for headings
    secondary: "#777777",     // Text Light for secondary text
    accent: "#2F80ED",       // Electric Blue for CTAs and highlights
    background: "#FFFFFF",   // Pure white for backgrounds
    softWhite: "#F9F9F9",    // Soft White for subtle backgrounds
    lightGray: "#E5E5E5",    // Light Gray for borders and dividers
    textDark: "#1C1C1C",     // Text Dark for body text
  },
  fonts: {
    primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    secondary: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  fontSizes: {
    hero: "clamp(2.5rem, 5vw, 4rem)",
    h1: "clamp(2rem, 4vw, 3rem)",
    h2: "clamp(1.5rem, 3vw, 2.25rem)",
    h3: "clamp(1.25rem, 2.5vw, 1.75rem)",
    body: "clamp(1rem, 2vw, 1.125rem)",
    small: "0.875rem",
  },
  spacing: {
    xs: "0.5rem",
    sm: "1rem",
    md: "1.5rem",
    lg: "2rem",
    xl: "3rem",
    xxl: "4rem",
  },
  borderRadius: {
    sm: "4px",
    md: "8px",
    lg: "12px",
  },
  transitions: {
    default: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
};

export const Styles = createGlobalStyle`
    /* Import Google Fonts */
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Poppins:wght@300;400;500;600;700&display=swap');

    @font-face {
        font-family: "Motiva Sans Light";
        src: url("/fonts/Motiva-Sans-Light.ttf") format("truetype");
        font-style: normal;
    }

    @font-face {
        font-family: "Motiva Sans Bold";
        src: url("/fonts/Motiva-Sans-Bold.ttf") format("truetype");
        font-style: normal;
    }

    /* Reset and base styles */
    *,
    *::before,
    *::after {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
    }

    html {
        scroll-behavior: smooth;
    }

    body {
        font-family: ${theme.fonts.primary};
        background-color: ${theme.colors.background};
        color: ${theme.colors.textDark};
        line-height: 1.6;
        overflow-x: hidden;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }

    /* Typography */
    h1, h2, h3, h4, h5, h6 {
        font-family: ${theme.fonts.secondary};
        font-weight: 600;
        line-height: 1.2;
        margin-bottom: ${theme.spacing.md};
        color: ${theme.colors.primary};
    }

    h1 {
        font-size: ${theme.fontSizes.hero};
        font-weight: 700;
        letter-spacing: -0.02em;
    }

    h2 {
        font-size: ${theme.fontSizes.h1};
        font-weight: 600;
    }

    h3 {
        font-size: ${theme.fontSizes.h2};
        font-weight: 500;
    }

    h4 {
        font-size: ${theme.fontSizes.h3};
        font-weight: 500;
    }

    p {
        font-size: ${theme.fontSizes.body};
        margin-bottom: ${theme.spacing.md};
        color: ${theme.colors.textDark};
        font-weight: 400;
        max-width: 65ch;
    }

    /* Links */
    a {
        color: ${theme.colors.accent};
        text-decoration: none;
        transition: ${theme.transitions.default};
        font-weight: 500;
    }

    a:hover {
        color: #0052CC;
        text-decoration: underline;
    }

    /* Form elements */
    input,
    textarea {
        font-family: ${theme.fonts.primary};
        font-size: ${theme.fontSizes.body};
        border: 1px solid ${theme.colors.lightGray};
        border-radius: ${theme.borderRadius.md};
        background-color: ${theme.colors.background};
        padding: ${theme.spacing.sm} ${theme.spacing.md};
        transition: ${theme.transitions.default};
        width: 100%;
        color: ${theme.colors.primary};
    }

    input:focus,
    textarea:focus {
        outline: none;
        border-color: ${theme.colors.accent};
        box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
    }

    /* Buttons */
    button {
        font-family: ${theme.fonts.primary};
        font-weight: 500;
        cursor: pointer;
        transition: ${theme.transitions.default};
        border: none;
        border-radius: ${theme.borderRadius.md};
    }

    /* Utility classes */
    .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 ${theme.spacing.md};
    }

    .section-padding {
        padding: ${theme.spacing.xl} 0;
    }

    /* Ant Design overrides */
    .ant-drawer-body {
        display: flex;
        flex-direction: column;
        text-align: left;
        padding-top: 1.5rem;
        font-family: ${theme.fonts.primary};
    }

    .ant-drawer-content-wrapper {
        width: 300px !important;
    }

    /* Component specific styles */
    .about-block-image svg {
        text-align: center;
    }

    /* Responsive adjustments */
    @media (max-width: 768px) {
        .section-padding {
            padding: ${theme.spacing.lg} 0;
        }
        
        h1 {
            font-size: ${theme.fontSizes.h1};
        }
        
        h2 {
            font-size: ${theme.fontSizes.h2};
        }
    }
`;
